import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { fetchActivity, type ActivityChanges, type ActivityEntry } from '@/features/activity/api'
import { fetchBoard } from '@/features/boards/api'
import { fetchPeople, type Person } from '@/features/people/api'
import type { ApiError } from '@/lib/api/client'
import type { Board } from '@/types/work'

/**
 * What has happened, newest first.
 *
 * The rows were always being written — every transition, comment, upload and
 * document records one — and the API has always served them. Nothing rendered
 * them, so the audit trail existed and could not be read, which is the same as
 * not having one.
 *
 * Deliberately thin on content: an activity row carries no visibility column of
 * its own, so the recorder keeps comment bodies and filenames out of it. This
 * shows who, what and when; what was actually said lives on the comment, where
 * the audience was chosen.
 */
export function ActivityFeed({
  project,
  subjectId,
}: {
  project?: string | undefined
  subjectId?: string | undefined
}) {
  const { t } = useTranslation()

  const activity = useQuery<ActivityEntry[], ApiError>({
    queryKey: ['activities', { project: project ?? null, subjectId: subjectId ?? null }],
    queryFn: () => fetchActivity({ project, subjectId }),
    retry: false,
  })

  // The stream stores what changed, which for a person or a column is a uuid.
  // "Assignee: nothing → …4ec5a3" is the row failing at the one thing it exists
  // to say, so the names come from the two lists the client already caches.
  // Neither is required: an unresolved id still renders, just tersely.
  const people = useQuery<Person[], ApiError>({
    queryKey: ['people'],
    queryFn: fetchPeople,
    retry: false,
  })

  const board = useQuery<Board, ApiError>({
    queryKey: ['board', project],
    queryFn: () => fetchBoard(project as string),
    enabled: project !== undefined,
    retry: false,
  })

  const names = new Map<string, string>([
    ...(people.data ?? []).map((person): [string, string] => [person.id, person.name]),
    ...(board.data?.columns ?? []).map((column): [string, string] => [column.id, column.name]),
  ])

  if (activity.data === undefined) {
    return <p className="text-ink-500 font-mono text-[11px]">{t('common.loading')}</p>
  }

  if (activity.data.length === 0) {
    return <p className="text-ink-500 text-[13px]">{t('activity.empty')}</p>
  }

  return (
    <ol className="flex flex-col gap-3">
      {activity.data.map((entry) => (
        <Row key={entry.id} entry={entry} names={names} />
      ))}
    </ol>
  )
}

function Row({ entry, names }: { entry: ActivityEntry; names: Map<string, string> }) {
  const { t, i18n } = useTranslation()

  // A verb with no label degrades to the verb itself rather than to a missing
  // translation key: the recorder's vocabulary grows, and a new one appearing
  // in the feed as `code_linked` beats it appearing as `activity.verb.x`.
  const verb = t(`activity.verb.${entry.verb}`, { defaultValue: entry.verb.replace(/_/g, ' ') })
  const subject = t(`activity.subject.${entry.subjectType}`, { defaultValue: entry.subjectType })

  return (
    <li className="flex gap-2.5">
      <Avatar name={entry.actor?.name ?? null} color={entry.actor?.color} size="sm" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="text-ink-900 text-[12px] font-medium">
            {entry.actor?.name ?? t('activity.unknownActor')}
          </span>
          <span className="text-ink-700 text-[12px]">{verb}</span>
          <span className="bg-surface-2 text-ink-500 rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase">
            {subject}
          </span>
          <time
            dateTime={entry.occurredAt}
            className="text-ink-500 font-mono text-[10px]"
            title={new Date(entry.occurredAt).toLocaleString(i18n.language)}
          >
            {new Date(entry.occurredAt).toLocaleDateString(i18n.language)}
          </time>
        </div>

        <Changes changes={entry.changes} names={names} />
      </div>
    </li>
  )
}

function Changes({ changes, names }: { changes: ActivityChanges | null; names: Map<string, string> }) {
  const { t } = useTranslation()

  const entries = Object.entries(changes ?? {})

  if (entries.length === 0) return null

  return (
    <dl className="text-ink-500 mt-0.5 flex flex-col gap-0.5 text-[11px]">
      {entries.map(([field, change]) => (
        <div key={field} className="flex flex-wrap items-baseline gap-x-1.5">
          <dt>{t(`activity.field.${field}`, { defaultValue: field.replace(/_/g, ' ') })}</dt>
          <dd className="flex items-baseline gap-1.5 font-mono">
            <Value value={change.from} names={names} />
            <span aria-hidden>→</span>
            <Value value={change.to} names={names} emphasis />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Value({
  value,
  names,
  emphasis = false,
}: {
  value: string | number | null
  names: Map<string, string>
  emphasis?: boolean
}) {
  const { t } = useTranslation()

  if (value === null || value === '') {
    return <span className="text-ink-500 italic">{t('activity.empty_value')}</span>
  }

  const raw = String(value)
  const name = names.get(raw)

  // A name where one is known. Failing that the tail of the id, which at least
  // lets two rows be told apart — better than a full uuid nobody can read.
  const isIdentifier = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(raw)

  return (
    <span className={emphasis ? 'text-ink-700' : undefined}>
      {name ?? (isIdentifier ? '…' + raw.slice(-6) : raw)}
    </span>
  )
}
